import { useEffect, useState } from "react";
import { X, FileDown, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal ligero para previsualizar el PDF de un diagnóstico sin descargar.
 * Descarga los bytes una vez, los convierte a blob URL y los muestra con
 * <object> (mejor compatibilidad con el visor nativo de Chrome/Edge que
 * <iframe>). Al cerrar, se libera el object URL.
 */
export default function PdfPreviewModal({ diagnosticId, filename, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let currentUrl = null;
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/diagnostic/${diagnosticId}/pdf`, {
          responseType: "blob",
        });
        if (cancelled) return;
        // Forzamos MIME correcto por si el servidor devolvió octet-stream.
        currentUrl = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" })
        );
        setBlobUrl(currentUrl);
      } catch (_e) {
        if (!cancelled) {
          setError(true);
          toast.error("No pudimos cargar la vista previa");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (currentUrl) window.URL.revokeObjectURL(currentUrl);
    };
  }, [diagnosticId]);

  // Cerrar con ESC
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  function downloadFromBlob() {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "talent-health-check.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function openInNewTab() {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  }

  // Sugerencia al visor nativo: ajusta ancho de página.
  const viewerUrl = blobUrl ? `${blobUrl}#view=FitH&toolbar=1` : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 fade-in-up"
      onClick={onClose}
      data-testid="pdf-preview-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[#0f6e56]/10 text-[#0f6e56] flex items-center justify-center flex-shrink-0">
              <FileDown className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-gray-900 text-[15px] truncate">
                Vista previa del informe
              </div>
              <div className="text-xs text-gray-500 truncate">
                {filename || "talent-health-check.pdf"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openInNewTab}
              disabled={!blobUrl}
              className="btn-secondary text-sm py-2 px-4 disabled:opacity-50 hidden sm:inline-flex"
              data-testid="pdf-preview-open-tab-btn"
              title="Abrir en pestaña nueva"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={2} /> Nueva pestaña
            </button>
            <button
              onClick={downloadFromBlob}
              disabled={!blobUrl}
              className="btn-primary text-sm py-2 px-4 disabled:opacity-50"
              data-testid="pdf-preview-download-btn"
            >
              <FileDown className="w-4 h-4" strokeWidth={2} /> Descargar
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              data-testid="pdf-preview-close-btn"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" strokeWidth={1.75} />
            </button>
          </div>
        </header>
        <div className="flex-1 bg-gray-100 relative">
          {!blobUrl && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin" strokeWidth={2} />
              <div className="text-sm">Preparando la vista previa…</div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center text-red-600 text-sm">
              No pudimos cargar el PDF. Prueba a descargarlo directamente.
            </div>
          )}
          {blobUrl && (
            <object
              data={viewerUrl}
              type="application/pdf"
              className="w-full h-full"
              data-testid="pdf-preview-iframe"
            >
              {/* Fallback si el navegador no tiene visor PDF nativo */}
              <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-8 text-center bg-gray-50">
                <div className="text-sm text-gray-700 max-w-md">
                  Tu navegador no puede previsualizar el PDF directamente.
                  Puedes abrirlo en una pestaña nueva o descargarlo.
                </div>
                <div className="flex gap-2">
                  <button onClick={openInNewTab} className="btn-secondary text-sm py-2 px-4">
                    <ExternalLink className="w-4 h-4" strokeWidth={2} /> Abrir en pestaña
                  </button>
                  <button onClick={downloadFromBlob} className="btn-primary text-sm py-2 px-4">
                    <FileDown className="w-4 h-4" strokeWidth={2} /> Descargar
                  </button>
                </div>
              </div>
            </object>
          )}
        </div>
      </div>
    </div>
  );
}
